import { uuid } from '@ctcells/core'
import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
} from '@tanstack/react-query'

import type { ApiError } from './errors.ts'

/**
 * 명령 훅의 **유일한 생성자**.
 *
 * 🔴 **낙관적 갱신을 구조로 막는다**(DP-04). 원시 `useMutation` 을 이 패키지 밖으로 내보내지 않고,
 *    여기서도 `onSuccess`·`onMutate` 를 **받지 않는다.** 호출부가 캐시를 손댈 구멍이 타입상 없다.
 *    성공하면 하는 일은 **무효화 하나**다 — 서버가 다시 완전한 집합을 준다.
 *
 * ⚠ 명령 응답은 대부분 **202 Accepted** 다(계약 7건). 결과는 스트림으로 온다 —
 *    애초에 낙관적으로 쓸 값이 응답에 없다. 구조가 규약과 같은 방향이다.
 *
 * ⚠ 무효화해도 **활성 관찰자가 없는 쿼리는 네트워크를 타지 않는다.** 알람 화면을 안 보고 있으면
 *    알람 키를 무효화해도 요청이 0건이다. 의도한 동작이다.
 */
export interface CommandDefinition<TVars, TResult> {
  /** 실제 요청. 여기서 `command_id`(멱등키)를 만들어 넣는다 */
  send: (vars: TVars) => Promise<TResult>
  /** 성공 후 비울 쿼리 키. 접두 일치라 도메인 루트를 주면 목록·상세가 같이 빠진다 */
  invalidates: (vars: TVars, result: TResult) => readonly QueryKey[]
}

export type CommandHook<TVars, TResult> = () => UseMutationResult<TResult, ApiError, TVars>

export function createCommand<TVars, TResult>(
  definition: CommandDefinition<TVars, TResult>,
): CommandHook<TVars, TResult> {
  return function useCommand() {
    const queryClient = useQueryClient()
    return useMutation<TResult, ApiError, TVars>({
      mutationFn: definition.send,
      onSuccess: (result, vars) => {
        for (const key of definition.invalidates(vars, result)) {
          void queryClient.invalidateQueries({ queryKey: key })
        }
      },
    })
  }
}

/**
 * 멱등키(IFC-MSG-04). 모든 상태변경 요청 본문에 실린다.
 *
 * ⚠ **재시도할 때 새로 만들지 않는다.** 계약이 「같은 `command_id` 재수신은 재실행 없이
 *    첫 결과 반환」으로 멱등을 보장하는데, 새 값을 만들면 그 보장이 깨진다.
 */
export function newCommandId(): string {
  /*
   * 🔴 **`crypto.randomUUID()` 를 직접 부르지 않는다**(2026-09-23 · eric 「획득 버튼을 누르면
   *    `crypto.randomUUID is not a function` 이라는 에러가 나는데?」). 그 함수는 **보안
   *    컨텍스트에서만** 있고, dev 서버에 **IP 로** 붙으면 통째로 `undefined` 다.
   *    갈래와 사유 전문은 `@ctcells/core` 의 `uuid.ts` 머리말에 있다.
   */
  return uuid()
}
